import * as React from 'react';
import { useForm as useReactHookForm, } from 'react-hook-form';
export function useForm(params) {
    const refContainer = React.useRef(null);
    const useReactHookFormReturn = useReactHookForm({
        defaultValues: params.defaultValues,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUseNativeValidation: false,
    });
    const { handleSubmit, reset, getValues, setValue } = useReactHookFormReturn;
    const [status, setStatus] = React.useState('initial');
    const [confirmationMessage, setConfirmationMessage] = React.useState('');
    const [serverErrors, setServerErrors] = React.useState([]);
    const [validationErrors, setValidationErrors] = React.useState([]);
    // Trim field values before validation runs, so trailing whitespace doesn't
    // trip up pattern rules (eg. NAME_RULE) that would otherwise block submission.
    const trimFormValues = () => {
        const values = getValues();
        Object.keys(values).forEach((key) => {
            const value = values[key];
            if (typeof value === 'string' && !/password/i.test(key)) {
                const trimmed = value.replace(/\s+$/, '');
                if (trimmed !== value) {
                    setValue(key, trimmed, { shouldValidate: false, shouldDirty: true });
                }
            }
        });
    };
    const resetForm = (keepMessage) => {
        setStatus('initial');
        setServerErrors([]);
        reset();
        if (!keepMessage) {
            setConfirmationMessage('');
        }
    };
    const onSubmit = async (formData, event) => {
        let res;
        if (params.mode === 'multipart') {
            const formData = new FormData(event?.target);
            const pairs = [];
            formData.forEach((value, key) => pairs.push([key, value]));
            pairs.forEach(([key, value]) => {
                if (typeof value === 'string' && !/password/i.test(key)) {
                    formData.set(key, value.replace(/\s+$/, ''));
                }
            });
            // We have to create the FormData instance before changing status
            // as disabled fields/fieldsets are not included otherwise.
            setStatus('pending');
            res = await fetch(params.endpoint, {
                body: formData,
                method: 'POST',
            });
        }
        else {
            setStatus('pending');
            res = await fetch(params.endpoint, {
                body: JSON.stringify(formData),
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
        }
        const resData = (await res.json());
        // If we're showing an error message then change to error.
        if (!res.ok) {
            setStatus('error');
            if (resData.validationErrors?.length) {
                setValidationErrors(resData.validationErrors);
            }
            if (resData.errors?.length) {
                setServerErrors(resData.errors);
            }
            if (!resData.validationErrors?.length && !resData.errors?.length) {
                setServerErrors([`Something went wrong.`]);
            }
            refContainer.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
            return;
        }
        // If we're showing an error message then change to error.
        if (resData.validationErrors?.length) {
            setStatus('error');
            setValidationErrors(resData.validationErrors);
            refContainer.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
            // At this stage we consider the submission unsuccessful so we
            // can trigger the optional callback.
            if (params.postErrorCallback) {
                await params.postErrorCallback();
            }
            return;
        }
        if (resData.errors?.length) {
            setStatus('error');
            setServerErrors(resData.errors);
            refContainer.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
            // At this stage we consider the submission unsuccessful so we
            // can trigger the optional callback.
            if (params.postErrorCallback) {
                await params.postErrorCallback();
            }
            return;
        }
        // If we're showing an error per field then change to error.
        if (resData.validationErrors?.length) {
            setStatus('error');
            setValidationErrors(resData.validationErrors);
            refContainer.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
            return;
        }
        // At this stage we consider the submission successful so we
        // can trigger the optional callback.
        if (params.postSubmitCallback) {
            await params.postSubmitCallback(resData);
        }
        // If we're showing a message then change to fulfilled.
        if (resData.message) {
            setStatus('fulfilled');
            setConfirmationMessage(resData.message);
            refContainer.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
            return;
        }
        // If we're redirecting leave status pending.
        if (resData.redirect) {
            window.location.href = resData.redirect;
            return;
        }
    };
    const submitForm = handleSubmit(onSubmit);
    return {
        confirmationMessage,
        refContainer,
        serverErrors,
        validationErrors,
        status,
        onSubmit: async (event) => {
            trimFormValues();
            return submitForm(event);
        },
        resetForm,
        useReactHookFormReturn,
    };
}
